import React, { useState, useEffect, useCallback } from 'react';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Save } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const TIER_ORDER = ["1","2","3","4","5","5-9","10-19","20-39","40-99","100-199","200+"];
const TIER_LABELS = {"1":"1","2":"2","3":"3","4":"4","5":"5","5-9":"5-9","10-19":"10-19","20-39":"20-39","40-99":"40-99","100-199":"100-199","200+":"200+"};

export default function ProductPricing() {
  const { t } = useLanguage();
  const [products, setProducts] = useState([]);
  const [pricings, setPricings] = useState({});
  const [saving, setSaving] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [pRes, prRes] = await Promise.all([
        axios.get(`${API}/api/products`, { withCredentials: true }),
        axios.get(`${API}/api/quotes/product-pricing`, { withCredentials: true })
      ]);
      setProducts(pRes.data);
      const map = {};
      prRes.data.forEach(p => {
        map[p.product_id] = p.tiers || {};
      });
      setPricings(map);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const updateTier = (productId, tier, value) => {
    setPricings(prev => ({
      ...prev,
      [productId]: { ...(prev[productId] || {}), [tier]: value }
    }));
  };

  const handleSave = async (productId) => {
    setSaving(productId);
    try {
      await axios.put(`${API}/api/quotes/product-pricing/${productId}`, { tiers: pricings[productId] || {} }, { withCredentials: true });
      toast.success(t('pricing_saved'));
    } catch (e) { toast.error('Erreur'); }
    finally { setSaving(null); }
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-semibold text-slate-900 tracking-tight">{t('product_pricing_title')}</h1>
          <p className="text-sm text-slate-600 mt-1">{t('product_pricing_desc')}</p>
        </div>

        {products.map(product => (
          <Card key={product.id} data-testid={`pricing-card-${product.id}`} className="border-slate-200 shadow-sm">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base font-medium">{product.name}</CardTitle>
                <Button data-testid={`save-pricing-${product.id}`} size="sm" className="bg-slate-900 hover:bg-slate-800 gap-1" onClick={() => handleSave(product.id)} disabled={saving === product.id}>
                  <Save size={14} /> {saving === product.id ? '...' : t('save')}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-4 sm:grid-cols-6 lg:grid-cols-11 gap-2">
                {TIER_ORDER.map(tier => (
                  <div key={tier} className="text-center">
                    <Label className="text-xs text-slate-500 block mb-1">{TIER_LABELS[tier]} pcs</Label>
                    <Input
                      data-testid={`price-${product.id}-${tier}`}
                      type="number"
                      step="0.01"
                      min="0"
                      className="text-center text-sm h-9"
                      value={pricings[product.id]?.[tier] || ''}
                      onChange={e => updateTier(product.id, tier, e.target.value)}
                      placeholder="0"
                    />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        ))}

        {products.length === 0 && (
          <p className="text-sm text-slate-500 text-center py-8">Aucun produit. Créez d'abord des produits.</p>
        )}
      </div>
    </Layout>
  );
}
